import {
  APIComponentAttributes,
  LoadedState,
  isTraversalLoadedState,
} from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import { StreamCollection } from 'streams/utils/streamUtils';
import { setCanvasSidebarQuery } from 'canvas/sidebarPanel/sidebars/contextSwitcher/actions';
import { getTypeQuery } from 'canvas/utils/getTypeQuery';
import { LoadedStateHistoryItem } from './historyTypes';
import { getTransactionIdFromSettings } from './getTransactionIdFromSetting';
import { pathOperations } from 'traversals/pathOperations';
import { anyType } from '@ardoq/global-consts';
import { isTruthy } from '@ardoq/common-helpers';

export type ChangesToAppViewState = {
  hasConditionalFormattingChanged: boolean;
  hasGroupBysChanged: boolean;
  hasLoadedStateChanged: boolean;
  hasViewNameChanged: boolean;
  hasViewSettingsChanged: boolean;
  hasChanges: boolean;
};

export const getChangesToAppViewState = (
  state: LoadedStateHistoryItem,
  lastPushedState: LoadedStateHistoryItem | null
): ChangesToAppViewState => {
  const {
    conditionalFormatting,
    groupBys,
    loadedState,
    viewName,
    viewSettings,
  } = state;

  const hasConditionalFormattingChanged =
    conditionalFormatting.transactionId !==
    lastPushedState?.conditionalFormatting?.transactionId;
  const hasGroupBysChanged =
    groupBys.transactionId !== lastPushedState?.groupBys?.transactionId;
  const hasLoadedStateChanged =
    loadedState.transactionId !== lastPushedState?.loadedState?.transactionId;
  const hasViewNameChanged = viewName !== lastPushedState?.viewName;
  const hasViewSettingsChanged =
    getTransactionIdFromSettings(viewSettings, viewName) !==
    getTransactionIdFromSettings(
      lastPushedState?.viewSettings,
      lastPushedState?.viewName
    );
  return {
    hasConditionalFormattingChanged,
    hasGroupBysChanged,
    hasLoadedStateChanged,
    hasViewNameChanged,
    hasViewSettingsChanged,
    hasChanges:
      hasConditionalFormattingChanged ||
      hasGroupBysChanged ||
      hasLoadedStateChanged ||
      hasViewNameChanged ||
      hasViewSettingsChanged,
  };
};

export const updateContextSwitcher = (
  loadedState: LoadedState,
  components: StreamCollection<APIComponentAttributes>
) => {
  const startType = getStartTypeFromLoadedState(loadedState, components);
  if (!startType) return;
  const query = getTypeQuery(startType);
  dispatchAction(
    setCanvasSidebarQuery({
      query,
    })
  );
};

export const getStartTypeFromLoadedState = (
  loadedState: LoadedState,
  components: StreamCollection<APIComponentAttributes>
) => {
  const startTypeFromPaths = isTraversalLoadedState(loadedState)
    ? pathOperations.getStartTypeFromPaths(loadedState.data.paths)
    : null;
  const startSetResult = loadedState.componentIdsAndReferences.startSetResult;
  const startSetTypeNames = startSetResult
    .map(id => components.byId[id]?.type ?? null)
    .filter(isTruthy);
  if (startTypeFromPaths !== anyType) {
    return startTypeFromPaths ?? startSetTypeNames[0] ?? null;
  }

  const startTypes = new Set(startSetTypeNames);
  if (startTypes.size === 1) return startSetTypeNames[0];
  return anyType;
};
